import React from 'react';
import {
  PictureAsPdf as PdfIcon,
  Dataset as DatasetIcon,
  CloudUpload as CloudUploadIcon,
  Apps as AppsIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Web as WebIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
  Audiotrack as AudiotrackIcon,
  Archive as ArchiveIcon,
  Description as DescriptionIcon,
  Slideshow as SlideshowIcon,
} from '@mui/icons-material';

// Common DataSource interface for icon rendering
export interface DataSourceForIcon {
  type?: string;
  storage_type?: string;
  provider?: string | null;
  app_type?: string;
  database_type?: string | null;
  file_type?: string;
  url?: string;
  filename?: string;
  name?: string;
}

/** Params for datasource download API: only one of gcp/azure/s3 is true when from that cloud; else all false */
export interface DownloadSourceParams {
  gcp: boolean;
  azure: boolean;
  s3: boolean;
}

/**
 * Maps datasource provider to download API query params. Returns gcp/azure/s3 booleans:
 * exactly one true when provider is that cloud, otherwise all false.
 */
export function getDownloadSourceParams(
  provider: string | null | undefined
): DownloadSourceParams {
  const p = (provider ?? "").toLowerCase().trim();
  if (["gcp", "gcs", "google_cloud", "google_cloud_storage"].includes(p)) {
    return { gcp: true, azure: false, s3: false };
  }
  if (["azure", "azure_blob", "azure_storage"].includes(p)) {
    return { gcp: false, azure: true, s3: false };
  }
  if (["s3", "aws", "amazon_s3"].includes(p)) {
    return { gcp: false, azure: false, s3: true };
  }
  return { gcp: false, azure: false, s3: false };
}

// Function to get file type from filename for icon selection
export const getFileTypeFromFilename = (filename: string): string => {
  if (!filename) return "file";
  const extension = filename.split(".").pop()?.toLowerCase();
  if (!extension) return "file";

  // Map common extensions to types
  const extensionMap: { [key: string]: string } = {
    pdf: "pdf",
    csv: "csv",
    xlsx: "spreadsheet",
    xls: "spreadsheet",
    doc: "document",
    docx: "document",
    ppt: "presentation",
    pptx: "presentation",
    txt: "document",
    html: "html",
    htm: "html",
    json: "json",
    xml: "json",
    jpg: "image",
    jpeg: "image",
    png: "image",
    gif: "image",
    svg: "image",
    mp4: "video",
    avi: "video",
    mov: "video",
    mp3: "audio",
    wav: "audio",
    zip: "archive",
    rar: "archive",
    "7z": "archive",
  };

  return extensionMap[extension] || "file";
};

/** File extensions that are previewable in-browser (PDF, images, txt only) */
const PREVIEW_SUPPORTED_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "txt",
];

/** MIME types that are previewable in-browser */
const PREVIEW_SUPPORTED_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "text/plain",
];

/**
 * Returns true if the datasource file can be displayed in-browser (modal).
 * PDF, images, txt only. Office docs (xlsx, docx, pptx) are not previewable
 * — Google Docs Viewer requires a publicly fetchable URL, blob URLs fail.
 */
export const isDatasourceSupportedForPreview = (
  datasource: DataSourceForIcon
): boolean => {
  if (datasource.file_type) {
    const mime = datasource.file_type.toLowerCase().split(";")[0].trim();
    if (PREVIEW_SUPPORTED_MIMES.includes(mime)) return true;
  }
  const filename = datasource.filename || datasource.name || "";
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return PREVIEW_SUPPORTED_EXTENSIONS.includes(ext);
};

// Function to map MIME types to icon types
export const getIconTypeFromMimeType = (mimeType: string): string => {
  if (!mimeType) return "file";

  const mimeTypeMap: { [key: string]: string } = {
    // PDF files
    "application/pdf": "pdf",

    // Spreadsheet files
    "application/vnd.ms-excel": "spreadsheet",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "spreadsheet",
    "text/csv": "csv",

    // Document files
    "application/msword": "document",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "document",
    "application/vnd.ms-powerpoint": "presentation",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "presentation",
    "text/plain": "document",

    // Web files
    "text/html": "html",
    "application/json": "json",
    "application/xml": "json",

    // Image files
    "image/jpeg": "image",
    "image/jpg": "image",
    "image/png": "image",
    "image/gif": "image",
    "image/svg+xml": "image",

    // Video files
    "video/mp4": "video",
    "video/avi": "video",
    "video/quicktime": "video",

    // Audio files
    "audio/mpeg": "audio",
    "audio/wav": "audio",

    // Archive files
    "application/zip": "archive",
    "application/x-rar-compressed": "archive",
    "application/x-7z-compressed": "archive",
  };

  return mimeTypeMap[mimeType] || "file";
};

// Function to get appropriate icon for datasource type
export const getDataSourceIcon = (datasource: DataSourceForIcon): React.ReactElement => {
  // Handle database: use provider for specific database images
  if (datasource.storage_type === "database") {
    const provider = datasource.provider?.toLowerCase() || datasource.database_type?.toLowerCase() || "";
    const databaseImages: { [key: string]: string } = {
      postgresql: "/images/apps/postgresql.png",
      mysql: "/images/apps/mysql.png",
      mariadb: "/images/apps/mariadb.png",
      oracle: "/images/apps/oracle.png",
      dynamodb: "/images/apps/dynamodb.png",
      redshift: "/images/apps/redshift.png",
    };
    const imagePath = databaseImages[provider];
    if (imagePath) {
      return (
        <img src={imagePath} alt={provider} style={{ width: 16, height: 16, flexShrink: 0 }} />
      );
    }
    // Fallback to generic database icon
    return (
      <DatasetIcon sx={{ fontSize: 16, color: "#1976d2", flexShrink: 0 }} />
    );
  }

  // Handle cloud: use provider for Google Drive, SharePoint, Azure, S3, GCP icons when present
  if (datasource.storage_type === "cloud") {
    const provider = datasource.provider?.toLowerCase() || "";
    const cloudAppImages: { [key: string]: string } = {
      google_drive: "/images/apps/gdrive.png",
      gdrive: "/images/apps/gdrive.png",
      sharepoint: "/images/apps/sharepoint.png",
      azure: "/images/apps/azure.png",
      azure_blob: "/images/apps/azure.png",
      azure_storage: "/images/apps/azure.png",
      s3: "/images/apps/s3.jpg",
      aws: "/images/apps/s3.jpg",
      amazon_s3: "/images/apps/s3.jpg",
      gcp: "/images/apps/gcp.png",
      gcs: "/images/apps/gcp.png",
      google_cloud: "/images/apps/gcp.png",
      google_cloud_storage: "/images/apps/gcp.png",
    };
    const imagePath = cloudAppImages[provider];
    if (imagePath) {
      return (
        <img src={imagePath} alt={provider} style={{ width: 16, height: 16, flexShrink: 0 }} />
      );
    }
    return (
      <CloudUploadIcon sx={{ fontSize: 16, color: "#0288d1", flexShrink: 0 }} />
    );
  }

  // Handle apps: use storage_type and app_type (incl. Google Drive, SharePoint)
  if (datasource.storage_type === "app") {
    const appType = (datasource.app_type || datasource.provider)?.toLowerCase() || "";
    const appImages: { [key: string]: string } = {
      gmail: "/images/apps/gmail.png",
      outlook: "/images/apps/outlook-v2.png",
      email: "/images/apps/email.png",
      google_drive: "/images/apps/gdrive.png",
      gdrive: "/images/apps/gdrive.png",
      sharepoint: "/images/apps/sharepoint.png",
    };
    const imagePath = appImages[appType];
    if (imagePath) {
      return (
        <img src={imagePath} alt={appType} style={{ width: 16, height: 16, flexShrink: 0 }} />
      );
    }
    return (
      <AppsIcon sx={{ fontSize: 16, color: "#7b1fa2", flexShrink: 0 }} />
    );
  }

  // Website datasources (webscrapping_UI branch) — the list endpoint never sends a `type`
  // field (only storage_type), so the `type === "url"` case further below is unreachable
  // for real API rows; check storage_type/url presence directly instead.
  if (datasource.storage_type === "website" || datasource.url) {
    return (
      <WebIcon sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }} />
    );
  }

  // Handle local/cloud with provider: show Google Drive, SharePoint, Azure, S3, GCP icons when applicable
  if ((datasource.storage_type === "local" || datasource.storage_type === "cloud") && datasource.provider) {
    const provider = datasource.provider?.toLowerCase() || "";
    const providerImages: { [key: string]: string } = {
      google_drive: "/images/apps/gdrive.png",
      gdrive: "/images/apps/gdrive.png",
      sharepoint: "/images/apps/sharepoint.png",
      azure: "/images/apps/azure.png",
      azure_blob: "/images/apps/azure.png",
      azure_storage: "/images/apps/azure.png",
      s3: "/images/apps/s3.jpg",
      aws: "/images/apps/s3.jpg",
      amazon_s3: "/images/apps/s3.jpg",
      gcp: "/images/apps/gcp.png",
      gcs: "/images/apps/gcp.png",
      google_cloud: "/images/apps/gcp.png",
      google_cloud_storage: "/images/apps/gcp.png",
    };
    const imagePath = providerImages[provider];
    if (imagePath) {
      return (
        <img src={imagePath} alt={provider} style={{ width: 16, height: 16, flexShrink: 0 }} />
      );
    }
    return (
      <CloudUploadIcon sx={{ fontSize: 16, color: "#0288d1", flexShrink: 0 }} />
    );
  }

  // Fallback to file type detection for regular files
  let iconType = datasource.type;

  // Use file_type if available, otherwise fall back to type or filename-based detection
  if (datasource.file_type) {
    iconType = getIconTypeFromMimeType(datasource.file_type);
  } else if (datasource.type === "file" && datasource.filename) {
    iconType = getFileTypeFromFilename(datasource.filename);
  }

  switch (iconType) {
    case "url":
      return (
        <WebIcon sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }} />
      );
    case "pdf":
      return <PdfIcon sx={{ fontSize: 16, color: "#d32f2f", flexShrink: 0 }} />;
    case "file":
      return (
        <InsertDriveFileIcon
          sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
        />
      );
    case "csv":
      return (
        <TableChartIcon
          sx={{ fontSize: 16, color: "#2e7d32", flexShrink: 0 }}
        />
      );
    case "html":
      return (
        <CodeIcon sx={{ fontSize: 16, color: "#1976d2", flexShrink: 0 }} />
      );
    case "json":
      return (
        <DataObjectIcon
          sx={{ fontSize: 16, color: "#ed6c02", flexShrink: 0 }}
        />
      );
    case "image":
      return (
        <ImageIcon sx={{ fontSize: 16, color: "#7b1fa2", flexShrink: 0 }} />
      );
    case "video":
      return (
        <VideoFileIcon sx={{ fontSize: 16, color: "#e91e63", flexShrink: 0 }} />
      );
    case "audio":
      return (
        <AudiotrackIcon
          sx={{ fontSize: 16, color: "#00bcd4", flexShrink: 0 }}
        />
      );
    case "archive":
    case "zip":
    case "rar":
      return (
        <ArchiveIcon sx={{ fontSize: 16, color: "#ff9800", flexShrink: 0 }} />
      );
    case "document":
      return (
        <DescriptionIcon
          sx={{ fontSize: 16, color: "#2196f3", flexShrink: 0 }}
        />
      );
    case "presentation":
      return (
        <SlideshowIcon
          sx={{ fontSize: 16, color: "#d24726", flexShrink: 0 }}
        />
      );
    case "spreadsheet":
      return (
        <TableChartIcon
          sx={{ fontSize: 16, color: "#4caf50", flexShrink: 0 }}
        />
      );
    default:
      return (
        <InsertDriveFileIcon
          sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
        />
      );
  }
};

